// The avatar shapes and accessories a person can choose, drawn in one 100-unit box as cubic
// beziers. Every shape holds TEXT_ZONES (two wide initials at each size in use) around its
// text centre, and no accessory crosses them; ui/__tests__/avatarShapes.test.ts checks both.
// Key lists must match migration 158's CHECKs and api/src/users/avatar-style.js.
// Doc: docs/features/user-avatars.md.

export const AVATAR_SHAPES = ['circle', 'square', 'hexagon', 'diamond', 'shield', 'heart', 'blossom', 'cloud', 'blob', 'burst'] as const;
export type AvatarShape = (typeof AVATAR_SHAPES)[number];
export const isAvatarShape = (v: unknown): v is AvatarShape => typeof v === 'string' && (AVATAR_SHAPES as readonly string[]).includes(v);

export const AVATAR_ACCESSORIES = ['halo', 'crown', 'party_hat', 'cat_ears', 'headphones', 'bow'] as const;
export type AvatarAccessory = (typeof AVATAR_ACCESSORIES)[number];
export const isAvatarAccessory = (v: unknown): v is AvatarAccessory => typeof v === 'string' && (AVATAR_ACCESSORIES as readonly string[]).includes(v);

/**
 * Half-width and half-height of "MW" ink, in box units, at each face size in use (9px in
 * 20px, 10px in 24px, 12px in 32px). Measured in the app's own font stack, which resolves
 * to the system sans; Ubuntu and Segoe are the widest of it.
 */
export const TEXT_ZONES = {
  20: { hw: 43, hh: 16 },
  24: { hw: 39.5, hh: 16.7 },
  32: { hw: 35.6, hh: 14.1 },
} as const;

export type Pt = [number, number];
type Seg = [Pt, Pt, Pt, Pt];
type Contour = Seg[];

// ── Geometry ───────────────────────────────────────────────────────────────

const add = (a: Pt, b: Pt): Pt => [a[0] + b[0], a[1] + b[1]];
const sub = (a: Pt, b: Pt): Pt => [a[0] - b[0], a[1] - b[1]];
const mul = (a: Pt, k: number): Pt => [a[0] * k, a[1] * k];
const len = (a: Pt) => Math.hypot(a[0], a[1]);
const unit = (a: Pt): Pt => mul(a, 1 / (len(a) || 1));
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const line = (a: Pt, b: Pt): Seg => [a, add(a, mul(sub(b, a), 1 / 3)), add(a, mul(sub(b, a), 2 / 3)), b];

/** A circular arc as cubics, a quarter turn at most per piece. Either direction. */
function arc(cx: number, cy: number, r: number, a0: number, a1: number): Seg[] {
  const n = Math.max(1, Math.ceil(Math.abs(a1 - a0) / (Math.PI / 2) - 1e-9));
  const out: Seg[] = [];
  for (let i = 0; i < n; i++) {
    const t0 = a0 + ((a1 - a0) * i) / n;
    const t1 = a0 + ((a1 - a0) * (i + 1)) / n;
    const k = (4 / 3) * Math.tan((t1 - t0) / 4) * r;
    const p0: Pt = [cx + r * Math.cos(t0), cy + r * Math.sin(t0)];
    const p3: Pt = [cx + r * Math.cos(t1), cy + r * Math.sin(t1)];
    out.push([p0, [p0[0] - k * Math.sin(t0), p0[1] + k * Math.cos(t0)], [p3[0] + k * Math.sin(t1), p3[1] - k * Math.cos(t1)], p3]);
  }
  return out;
}

const circle = (cx: number, cy: number, r: number): Contour => arc(cx, cy, r, -Math.PI / 2, (3 * Math.PI) / 2);
const ellipse = (cx: number, cy: number, rx: number, ry: number, reverse = false): Contour =>
  map(arc(0, 0, 1, reverse ? (3 * Math.PI) / 2 : -Math.PI / 2, reverse ? -Math.PI / 2 : (3 * Math.PI) / 2), ([x, y]) => [cx + rx * x, cy + ry * y]);

const normAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

/** A polygon with each corner rounded by its radius (one number, or one per vertex). */
function rounded(vertices: Pt[], radius: number | number[]): Contour {
  const n = vertices.length;
  const corners = vertices.map((c, i) => {
    const r = Array.isArray(radius) ? radius[i] : radius;
    const u = unit(sub(vertices[(i + n - 1) % n], c));
    const v = unit(sub(vertices[(i + 1) % n], c));
    const half = Math.acos(clamp(u[0] * v[0] + u[1] * v[1], -1, 1)) / 2;
    const t = r / Math.tan(half);
    const a = add(c, mul(u, t));
    const b = add(c, mul(v, t));
    if (r <= 0) return { a: c, b: c, segs: [] as Seg[] };
    const centre = add(c, mul(unit(add(u, v)), r / Math.sin(half)));
    const a0 = Math.atan2(a[1] - centre[1], a[0] - centre[0]);
    const a1 = a0 + normAngle(Math.atan2(b[1] - centre[1], b[0] - centre[0]) - a0);
    return { a, b, segs: arc(centre[0], centre[1], r, a0, a1) };
  });
  const out: Contour = [];
  corners.forEach((c, i) => {
    const prev = corners[(i + n - 1) % n];
    if (len(sub(c.a, prev.b)) > 1e-6) out.push(line(prev.b, c.a));
    out.push(...c.segs);
  });
  return out;
}

/** A smooth closed curve through evenly spaced samples (Catmull-Rom, as cubics). */
function smooth(points: Pt[]): Contour {
  const n = points.length;
  return points.map((p1, i): Seg => {
    const p0 = points[(i + n - 1) % n];
    const p2 = points[(i + 1) % n];
    const p3 = points[(i + 2) % n];
    return [p1, add(p1, mul(sub(p2, p0), 1 / 6)), sub(p2, mul(sub(p3, p1), 1 / 6)), p2];
  });
}

/** An authored path: absolute M, C, L and Z only. */
export function path(d: string): Contour {
  const tok = d.match(/[MCLZ]|-?\d*\.?\d+/g) || [];
  const out: Contour = [];
  let i = 0;
  let cur: Pt = [0, 0];
  let start: Pt = [0, 0];
  let cmd = '';
  const num = () => {
    if (i >= tok.length || /[MCLZ]/.test(tok[i])) throw new Error(`avatar path ends inside "${cmd}": ${d}`);
    return Number(tok[i++]);
  };
  while (i < tok.length) {
    const at = i;
    if (/[MCLZ]/.test(tok[i])) cmd = tok[i++];
    if (cmd === 'M') { cur = [num(), num()]; start = cur; cmd = 'L'; }
    else if (cmd === 'L') { const p: Pt = [num(), num()]; out.push(line(cur, p)); cur = p; }
    else if (cmd === 'C') { const s: Seg = [cur, [num(), num()], [num(), num()], [num(), num()]]; out.push(s); cur = s[3]; }
    else if (cmd === 'Z') { if (len(sub(cur, start)) > 1e-6) out.push(line(cur, start)); cur = start; cmd = ''; }
    // A number no command reads (one before the first M, or after a Z) would otherwise spin here.
    if (i === at || (cmd === '' && i < tok.length && !/[MCLZ]/.test(tok[i]))) throw new Error(`avatar path: unexpected "${tok[i]}" at token ${i}: ${d}`);
  }
  return out;
}

function map(c: Contour, f: (p: Pt) => Pt): Contour {
  return c.map((s) => s.map(f) as Seg);
}

/** Rotate by `deg` about `pivot`, then move by `by`. */
const place = (c: Contour, pivot: Pt, deg: number, by: Pt): Contour => {
  const a = (deg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return map(c, ([x, y]) => {
    const dx = x - pivot[0];
    const dy = y - pivot[1];
    return [pivot[0] + dx * cos - dy * sin + by[0], pivot[1] + dx * sin + dy * cos + by[1]];
  });
};

interface Circ { x: number; y: number; r: number }

/** Where circles A and B, grown by f, cross: the centre of a fillet of radius f between them. */
function filletCentre(A: Circ, B: Circ, f: number, away: Pt): Pt {
  const R1 = A.r + f;
  const R2 = B.r + f;
  const d = Math.hypot(B.x - A.x, B.y - A.y);
  const a = (R1 * R1 - R2 * R2 + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, R1 * R1 - a * a));
  const ex = (B.x - A.x) / d;
  const ey = (B.y - A.y) / d;
  const px = A.x + a * ex;
  const py = A.y + a * ey;
  const s1: Pt = [px - h * ey, py + h * ex];
  const s2: Pt = [px + h * ey, py - h * ex];
  return len(sub(s1, away)) > len(sub(s2, away)) ? s1 : s2;
}

/**
 * The outline of a chain of overlapping circles, each joint softened by a fillet. Traversed
 * clockwise on screen. Closed: the chain wraps (a blossom). Open: it starts at the first
 * circle's bottom and ends at the last one's, closed by a flat base (a cloud).
 */
function circleChain(cs: Circ[], f: number, closed: boolean, centre: Pt): Contour {
  const n = cs.length;
  const joints = cs.slice(0, closed ? n : n - 1).map((A, i) => {
    const B = cs[(i + 1) % n];
    const F = filletCentre(A, B, f, centre);
    const ta = add([A.x, A.y], mul(unit(sub(F, [A.x, A.y])), A.r));
    const tb = add([B.x, B.y], mul(unit(sub(F, [B.x, B.y])), B.r));
    return { F, ta, tb };
  });
  const ang = (C: Circ, p: Pt) => Math.atan2(p[1] - C.y, p[0] - C.x);
  const cw = (from: number, to: number) => from + (((to - from) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  const out: Contour = [];
  cs.forEach((C, i) => {
    const entry = closed ? joints[(i + n - 1) % n].tb : i === 0 ? ([C.x, C.y + C.r] as Pt) : joints[i - 1].tb;
    const exit = i < joints.length ? joints[i].ta : ([C.x, C.y + C.r] as Pt);
    const a0 = ang(C, entry);
    out.push(...arc(C.x, C.y, C.r, a0, cw(a0, ang(C, exit))));
    if (i < joints.length) {
      const { F, ta, tb } = joints[i];
      const f0 = Math.atan2(ta[1] - F[1], ta[0] - F[0]);
      out.push(...arc(F[0], F[1], f, f0, f0 + normAngle(Math.atan2(tb[1] - F[1], tb[0] - F[0]) - f0)));
    }
  });
  if (!closed) {
    const first = cs[0];
    const last = cs[n - 1];
    out.push(line([last.x, last.y + last.r], [first.x, first.y + first.r]));
  }
  return out;
}

const fmt = (v: number) => (Math.round(v * 10) / 10).toString();
const toD = (contours: Contour[]): string =>
  contours
    .map((c) => `M${fmt(c[0][0][0])} ${fmt(c[0][0][1])}${c.map((s) => `C${s.slice(1).map((p) => `${fmt(p[0])} ${fmt(p[1])}`).join(' ')}`).join('')}Z`)
    .join('');

/** The contour as points, for containment checks and anchors. */
export function flatten(c: Contour, steps = 10): Pt[] {
  const out: Pt[] = [];
  for (const [p0, p1, p2, p3] of c) {
    for (let k = 0; k < steps; k++) {
      const t = k / steps;
      const u = 1 - t;
      out.push([
        u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
        u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
      ]);
    }
  }
  return out;
}

/** True when (x, y) is inside the outline (even-odd ray cast). */
export function inside(points: Pt[], x: number, y: number): boolean {
  let hit = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** Where a horizontal line at y crosses the outline: [leftmost, rightmost]. */
function spanAt(pts: Pt[], y: number): [number, number] {
  const xs: number[] = [];
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y) xs.push(xi + ((y - yi) * (xj - xi)) / (yj - yi));
  }
  return xs.length ? [Math.min(...xs), Math.max(...xs)] : [50, 50];
}

/** The highest point of the outline over the columns x0..x1. */
function topOver(pts: Pt[], x0: number, x1: number): number {
  let top = Infinity;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    for (const x of [x0, (x0 + x1) / 2, x1]) {
      const [xi, yi] = pts[i];
      const [xj, yj] = pts[j];
      if (xi > x !== xj > x) top = Math.min(top, yi + ((x - xi) * (yj - yi)) / (xj - xi));
    }
    if (pts[i][0] >= x0 && pts[i][0] <= x1) top = Math.min(top, pts[i][1]);
  }
  return top;
}

/** Where a ray from `from` at angle `deg` leaves the outline. */
function exitAlong(pts: Pt[], from: Pt, deg: number): Pt {
  const a = (deg * Math.PI) / 180;
  let d = 0;
  while (d < 80 && inside(pts, from[0] + d * Math.cos(a), from[1] + d * Math.sin(a))) d += 0.25;
  return [from[0] + d * Math.cos(a), from[1] + d * Math.sin(a)];
}

// ── The shapes ─────────────────────────────────────────────────────────────
// Sized by eye against the circle, keyline-style: the square sits at 90 because a square
// reads heavier than a circle of the same box; pointed shapes overshoot a little because a
// point carries almost no weight. The initials' band decides the rest: every shape needs a
// near full-width middle, so the character lives in the top and bottom silhouettes.

const C: Pt = [50, 50];

const SHAPE_CONTOURS: Record<AvatarShape, Contour> = {
  circle: circle(50, 50, 50),

  square: rounded([[5, 5], [95, 5], [95, 95], [5, 95]], 14),

  /** Pointed top and bottom, upright sides: the sides give the initials the full height of the band. */
  hexagon: rounded([0, 1, 2, 3, 4, 5].map((k) => {
    const a = ((k * 60 - 90) * Math.PI) / 180;
    return [50 + 54 * Math.cos(a), 50 + 54 * Math.sin(a)] as Pt;
  }), 9),

  /** A soft diamond: a superellipse under 2, so the faces bow out enough for the band. */
  diamond: smooth(Array.from({ length: 48 }, (_, i) => {
    const t = (i / 48) * 2 * Math.PI;
    const e = 2 / 1.62;
    const x = Math.sign(Math.cos(t)) * Math.abs(Math.cos(t)) ** e;
    const y = Math.sign(Math.sin(t)) * Math.abs(Math.sin(t)) ** e;
    return [50 + 53.5 * x, 50 + 53.5 * y] as Pt;
  })),

  /** A heater shield: a straight top with softened corners, upright sides, a gothic point. */
  shield: path(
    'M18 3 L82 3 C90 3 96 8 96 16 L96 54 C96 77 77 92 52 100.5 C50.7 101 49.3 101 48 100.5 C23 92 4 77 4 54 L4 16 C4 8 10 3 18 3 Z',
  ),

  /** Full lobes over a softly rounded point. */
  heart: path(
    'M46.5 95.5 C35 88 -3 66 -3 37 C-3 17 11 4 28 4 C39 4 46.5 10.5 50 18.5 C53.5 10.5 61 4 72 4 C89 4 103 17 103 37 C103 66 65 88 53.5 95.5 C51.4 96.9 48.6 96.9 46.5 95.5 Z',
  ),

  /** Six round petals with softened notches; the notches fall where initials never reach. */
  blossom: circleChain(
    [0, 1, 2, 3, 4, 5].map((k) => {
      const a = ((k * 60) * Math.PI) / 180;
      return { x: 50 + 28 * Math.cos(a), y: 50 + 28 * Math.sin(a), r: 24.5 };
    }),
    2.5, true, C,
  ),

  /** One big lobe right of centre, a smaller one leading, a low one trailing, on a flat base. */
  cloud: circleChain(
    [
      { x: 29, y: 64, r: 31 },
      { x: 42, y: 38, r: 21 },
      { x: 63, y: 35, r: 27 },
      { x: 73, y: 65, r: 30 },
    ],
    3, false, C,
  ),

  /** A river pebble: a wide, slightly tipped oval with one fuller end. */
  blob: smooth(Array.from({ length: 36 }, (_, i) => {
    const t = (i / 36) * 2 * Math.PI;
    const x = 52 * Math.cos(t);
    const y = 45 * Math.sin(t) * (1 + 0.07 * Math.cos(t));
    const a = (-7 * Math.PI) / 180;
    return [50 + x * Math.cos(a) - y * Math.sin(a), 51 + x * Math.sin(a) + y * Math.cos(a)] as Pt;
  })),

  /** A twelve-point sunburst, a point straight up; shallow, so the band keeps its width. */
  burst: smooth(Array.from({ length: 96 }, (_, i) => {
    const a = (i / 96) * 2 * Math.PI;
    const f = ((1 + Math.cos(12 * (a + Math.PI / 2))) / 2) ** 1.5;
    const r = 47.3 + 6.3 * f;
    return [50 + r * Math.cos(a), 50 + r * Math.sin(a)] as Pt;
  })),
};

/** Where the initials sit, as an offset of their centre from the box centre, in box units. */
const TEXT_DY: Partial<Record<AvatarShape, number>> = { shield: -7, heart: -10, cloud: 13 };
/** Where a hat sits, when not the middle: the cloud's crown is its big lobe. */
const CROWN_X: Partial<Record<AvatarShape, number>> = { cloud: 60 };
const CROWN_TILT: Partial<Record<AvatarShape, number>> = { cloud: 6 };

export const SHAPE_LABELS: Record<AvatarShape, string> = {
  circle: 'Circle', square: 'Square', hexagon: 'Hexagon', diamond: 'Diamond', shield: 'Shield',
  heart: 'Heart', blossom: 'Blossom', cloud: 'Cloud', blob: 'Pebble', burst: 'Sunburst',
};

export interface ShapeSpec {
  key: AvatarShape;
  label: string;
  /** SVG path in the 0 0 100 100 box. */
  d: string;
  /** The outline as points, for the checks and the accessory anchors. */
  points: Pt[];
  /** The initials' centre offset from the box centre, in box units. */
  dy: number;
}

export const SHAPES = Object.fromEntries(AVATAR_SHAPES.map((key) => {
  const c = SHAPE_CONTOURS[key];
  return [key, { key, label: SHAPE_LABELS[key], d: toD([c]), points: flatten(c), dy: TEXT_DY[key] || 0 }];
})) as Record<AvatarShape, ShapeSpec>;

/** The text zone at a size, as a rect around the shape's text centre, grown by `grow`. */
export function textRect(shape: ShapeSpec, size: keyof typeof TEXT_ZONES, grow = 0) {
  const { hw, hh } = TEXT_ZONES[size];
  const cy = 50 + shape.dy;
  return { x0: 50 - hw - grow, x1: 50 + hw + grow, y0: cy - hh - grow, y1: cy + hh + grow };
}

/** The widest margin m for which the text zone at `size`, grown by m, still fits inside the shape. */
export function safeMargin(shape: ShapeSpec, size: keyof typeof TEXT_ZONES): number {
  let m = -1;
  for (let grow = 0; grow <= 20; grow += 0.25) {
    const { x0, x1, y0, y1 } = textRect(shape, size, grow);
    let ok = true;
    for (let k = 0; k <= 40 && ok; k++) {
      const f = k / 40;
      const xs = x0 + (x1 - x0) * f;
      const ys = y0 + (y1 - y0) * f;
      ok = inside(shape.points, xs, y0) && inside(shape.points, xs, y1) && inside(shape.points, x0, ys) && inside(shape.points, x1, ys);
    }
    if (!ok) break;
    m = grow;
  }
  return m;
}

/** Area in square box units (shoelace), for matching optical weight. */
export function area(points: Pt[]): number {
  let s = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) s += points[j][0] * points[i][1] - points[i][0] * points[j][1];
  return Math.abs(s) / 2;
}

// ── Accessories ────────────────────────────────────────────────────────────
// Drawn per shape from three anchors read off its outline: the crown (where a hat sits and
// how wide the head is there), the sides at the initials' height, and a shoulder up and to
// the right. `back` parts sit behind the face, `front` parts over it; neither may cross the
// initials.

/** What a part is painted with; Avatar resolves each to a token. */
export type AccessoryFill = 'accent' | 'light' | 'mid' | 'white' | 'face' | 'faceInner';

export interface AccessoryPart { d: string; fill: AccessoryFill; glow?: boolean; points: Pt[] }
export interface AccessoryArt { back: AccessoryPart[]; front: AccessoryPart[] }

export const ACCESSORY_LABELS: Record<AvatarAccessory, string> = {
  halo: 'Halo', crown: 'Crown', party_hat: 'Party hat',
  cat_ears: 'Cat ears', headphones: 'Headphones', bow: 'Bow',
};

interface Anchors { top: Pt; tilt: number; w: number; left: number; right: number; ty: number; shoulder: Pt; pts: Pt[] }

function anchorsFor(shape: ShapeSpec): Anchors {
  const pts = shape.points;
  const cx = CROWN_X[shape.key] ?? 50;
  const topY = topOver(pts, cx - 9, cx + 9);
  const [l, r] = spanAt(pts, topY + 11);
  const ty = 50 + shape.dy;
  const [left, right] = spanAt(pts, ty);
  return {
    top: [cx, topY], tilt: CROWN_TILT[shape.key] || 0, w: (r - l) / 2, left, right, ty, pts,
    shoulder: exitAlong(pts, [50, ty], -47),
  };
}

const part = (contours: Contour[], fill: AccessoryFill, glow = false): AccessoryPart =>
  ({ d: toD(contours), fill, glow, points: contours.flatMap((c) => flatten(c, 6)) });

const grow = (c: Contour, k: number): Contour => map(c, ([x, y]) => [x * k, y * k]);
/** A hat-like thing drawn around (0, 0) at a scale, set on the crown and tipped with the shape. */
const onCrown = (a: Anchors, c: Contour, extraTilt = 0, k = 1): Contour => place(grow(c, k), [0, 0], a.tilt + extraTilt, a.top);
/** The top of the initials at the densest size: nothing worn on the head comes lower. */
const textTop = (a: Anchors) => a.ty - TEXT_ZONES[20].hh - 4;

const ACCESSORY_DRAW: Record<AvatarAccessory, (a: Anchors) => AccessoryArt> = {
  halo: (a) => {
    const rx = clamp(a.w + 5, 24, 36);
    return {
      back: [],
      front: [part([onCrown(a, ellipse(0, -15, rx, 8.5)), onCrown(a, ellipse(0, -15.8, rx - 6.5, 4, true))], 'accent', true)],
    };
  },

  crown: (a) => {
    const cw = clamp(a.w * 0.62, 13, 18);
    const body = rounded([[-cw, 5], [-cw, -10], [-cw * 0.47, -2.5], [0, -15], [cw * 0.47, -2.5], [cw, -10], [cw, 5]], [2.2, 1.6, 1.6, 1.6, 1.6, 1.6, 2.2]);
    const gems = [circle(-cw, -11.2, 3.4), circle(0, -16.5, 3.6), circle(cw, -11.2, 3.4)];
    return { back: [], front: [part([body, ...gems].map((c) => onCrown(a, c, -9, 1.3)), 'accent')] };
  },

  party_hat: (a) => {
    const cone = rounded([[-12.5, 5], [3, -29], [12.5, 5]], [3, 2.5, 3]);
    const band = rounded([[-11.2, 2.2], [-8.4, -4.2], [9, -4.2], [11.4, 2.2]], 0.8);
    const k = 1.3;
    return {
      back: [],
      front: [
        part([onCrown(a, cone, 14, k)], 'accent'),
        part([onCrown(a, band, 14, k)], 'white'),
        part([onCrown(a, circle(3, -30, 5.2), 14, k)], 'white'),
      ],
    };
  },

  cat_ears: (a) => {
    const ew = clamp(a.w * 0.8, 17, 30);
    const ear = (side: -1 | 1, inner: boolean): Contour => {
      const x = a.top[0] + side * ew;
      const y = topOver(a.pts, x - 1, x + 1) + 5;
      const tri = inner
        ? rounded([[-7.5, -5], [0, -28], [7.5, -5]], [1.5, 2.6, 1.5])
        : rounded([[-15, 0], [0, -38], [15, 0]], [3, 5.5, 3]);
      return place(tri, [0, 0], side * 22 + a.tilt, [x, y]);
    };
    return {
      back: [part([ear(-1, false), ear(1, false)], 'face'), part([ear(-1, true), ear(1, true)], 'faceInner')],
      front: [],
    };
  },

  headphones: (a) => {
    // Cups beside the face, low at the jaw, never reaching into the widest initials.
    const limit = 50 - TEXT_ZONES[20].hw - 1.5;
    const lIn = Math.min(a.left + 4, limit);
    const rIn = Math.max(a.right - 4, 100 - limit);
    const cy = a.ty + 4;
    const cupL = rounded([[lIn - 13, cy - 13], [lIn, cy - 13], [lIn, cy + 13], [lIn - 13, cy + 13]], 6);
    const cupR = rounded([[rIn, cy - 13], [rIn + 13, cy - 13], [rIn + 13, cy + 13], [rIn, cy + 13]], 6);
    // The band: outer and inner cubics, apexes held a set gap above the crown.
    const lx = lIn - 6.5;
    const rx = rIn + 6.5;
    const foot = cy - 11;
    const ctrl = (apex: number) => (apex - 0.25 * foot) / 0.75;
    const outer = ctrl(a.top[1] - 13);
    const inner = ctrl(a.top[1] - 7);
    const band = path(
      `M${lx - 3.2} ${foot} C${lx - 3.2} ${outer} ${rx + 3.2} ${outer} ${rx + 3.2} ${foot} L${rx - 3.2} ${foot} C${rx - 3.2} ${inner} ${lx + 3.2} ${inner} ${lx + 3.2} ${foot} Z`,
    );
    return { back: [part([band], 'mid')], front: [part([cupL, cupR], 'light')] };
  },

  bow: (a) => {
    const loop = (side: -1 | 1) => rounded([[0, 0], [side * 13, -9], [side * 13, 9]], [1, 4, 4]);
    const at = (c: Contour) => place(grow(c, 1.4), [0, 0], 28, a.shoulder);
    return {
      back: [],
      front: [part([at(loop(-1)), at(loop(1))], 'accent'), part([at(circle(0, 0, 3.8))], 'white')],
    };
  },

};

const ART_CACHE = new Map<string, AccessoryArt>();

export function accessoryArt(shape: AvatarShape, accessory: AvatarAccessory): AccessoryArt {
  const key = `${shape}:${accessory}`;
  let art = ART_CACHE.get(key);
  if (!art) {
    art = ACCESSORY_DRAW[accessory](anchorsFor(SHAPES[shape]));
    ART_CACHE.set(key, art);
  }
  return art;
}

/** How far a front part comes to the initials' zone at a size: negative means it crosses. */
export function accessoryClearance(shape: AvatarShape, accessory: AvatarAccessory, size: keyof typeof TEXT_ZONES): number {
  const { x0, x1, y0, y1 } = textRect(SHAPES[shape], size);
  let best = Infinity;
  for (const p of accessoryArt(shape, accessory).front) {
    for (const [x, y] of p.points) {
      const dx = Math.max(x0 - x, 0, x - x1);
      const dy = Math.max(y0 - y, 0, y - y1);
      const d = dx === 0 && dy === 0 ? -Math.min(x - x0, x1 - x, y - y0, y1 - y) : Math.hypot(dx, dy);
      best = Math.min(best, d);
    }
  }
  return best;
}

/** How far above the box the tallest accessory reaches, in box units, for stack headroom. */
export const ACCESSORY_HEADROOM = 40;
